import type { SingleSearchRequest, SingleSearchResult } from '@/types';
import { collectabilityAPI } from './collectabilityAPI';
import { logger, ValidationError, NetworkError } from '@/utils/logger';

const ACCOUNT_SERVICE_URL = import.meta.env.VITE_ACCOUNT_SERVICE_URL;

/**
 * Service for single person search operations
 * Calls the real CollectABILITY API and retrieves from DynamoDB via account-service Lambda
 */
class SingleSearchAPIService {
  private searchResults: Map<string, SingleSearchResult> = new Map();

  /**
   * Create a new search by calling the real API
   * Returns the full search result for saving to history
   */
  async createSearch(request: SingleSearchRequest): Promise<SingleSearchResult> {
    if (!request.firstName || !request.lastName) {
      throw new ValidationError('First name and last name are required', 'name');
    }

    // Validate search type
    const validSearchTypes = ['validate', 'enrich', 'enrich_validate', 'scoring', 'scoring_enrich'];
    if (!request.searchType || !validSearchTypes.includes(request.searchType)) {
      throw new ValidationError('Invalid search type', 'searchType');
    }

    // Amount to collect is only required for scoring operations
    if (request.searchType === 'scoring' || request.searchType === 'scoring_enrich') {
      if (!request.amountToCollect || request.amountToCollect <= 0) {
        throw new ValidationError('Amount to collect is required for scoring operations', 'amountToCollect');
      }
    }

    logger.info('Creating new search with real API', {
      firstName: request.firstName,
      lastName: request.lastName,
      searchType: request.searchType,
      amountToCollect: request.amountToCollect,
    });

    // Call the real CollectABILITY API
    const result = await collectabilityAPI.performSearch(request);

    // Store in memory cache for quick access
    this.searchResults.set(result.requestId, result);

    // NOTE: Search history saving is handled by SearchPaymentPage/SearchPaymentCheckoutForm
    // after payment succeeds using accountServiceAPI.saveSearchHistory() with full search result
    // This ensures we only save searches that were actually paid for and includes correct cost/user info

    logger.info('Search created successfully', {
      searchId: result.id,
      requestId: result.requestId,
      score: result.collectabilityScore
    });

    // Return the full search result so payment page can save it to history
    return result;
  }

  /**
   * Process search (no-op since API call is synchronous)
   */
  async processSearch(searchId: string): Promise<void> {
    const result = this.searchResults.get(searchId);

    if (!result) {
      logger.error(`Search not found: ${searchId}`);
      throw new ValidationError(`Search not found: ${searchId}`, 'searchId');
    }

    logger.info(`Search already processed: ${searchId}`);
    // No processing needed - API returns results immediately
  }

  /**
   * Get search results by search ID
   * Fetches from CollectABILITY_SearchHistory table via account-service Lambda
   */
  async getSearchResults(searchId: string): Promise<SingleSearchResult> {
    logger.info(`Fetching search results for searchId: ${searchId}`);

    // Check memory cache first
    const cached = this.searchResults.get(searchId);
    if (cached) {
      logger.info(`Returning cached search results: ${searchId}`);
      return cached;
    }

    // Fetch from DynamoDB via account-service Lambda
    if (!ACCOUNT_SERVICE_URL) {
      throw new NetworkError('Account service URL not configured');
    }

    try {
      const response = await fetch(ACCOUNT_SERVICE_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          operation: 'get_search',
          searchId, // Updated to use searchId
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        logger.error(`Failed to fetch search from DynamoDB: ${response.status}`, { errorText });
        throw new NetworkError(`Failed to fetch search: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();

      if (!data.success || !data.search) {
        logger.error(`Search not found in DynamoDB: ${searchId}`);
        throw new ValidationError(`Search results not found: ${searchId}`, 'searchId');
      }

      // The search result from our dedicated table
      const result = data.search as SingleSearchResult;

      // Log what we received for debugging
      logger.info('Received search data from DynamoDB', {
        searchId,
        hasSearchRequest: !!result.searchRequest,
        hasId: !!result.id,
        hasPrimaryVerified: !!result.primaryVerified,
        keys: Object.keys(result),
        dataKeys: Object.keys(data),
      });

      // Validate the result has required fields
      if (!result.searchRequest) {
        logger.error('DynamoDB record missing searchRequest field - likely backend storage issue', {
          searchId,
          hasId: !!result.id,
          hasTimestamp: !!result.timestamp,
          hasScore: !!result.collectabilityScore,
          keys: Object.keys(result),
          fullData: JSON.stringify(data).substring(0, 500), // First 500 chars for debugging
        });
        throw new ValidationError(
          'This search report is no longer available for viewing. The search was completed successfully, but the full report data was not stored properly. Please run a new search if you need to view the results again.',
          'searchRequest'
        );
      }

      // Cache for future requests
      this.searchResults.set(searchId, result);

      logger.info(`Search results retrieved from CollectABILITY_SearchHistory: ${searchId}`);

      return result;
    } catch (error) {
      if (error instanceof ValidationError || error instanceof NetworkError) {
        throw error;
      }

      logger.error('Error fetching search from DynamoDB:', error);
      throw new NetworkError(`Failed to fetch search: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Get search history for a user (for logged-in users in future)
   */
  async getSearchHistory(userId: string): Promise<SingleSearchResult[]> {
    logger.info(`Fetching search history for user: ${userId}`);

    // In-memory store - return all searches for now
    // In production, this would query a database filtered by userId
    const history = Array.from(this.searchResults.values()).slice(0, 10);

    logger.info(`Retrieved ${history.length} search results for user ${userId}`);

    return history;
  }

  /**
   * Delete a search result from cache (cleanup)
   */
  deleteSearch(requestId: string): void {
    logger.info(`Deleting search from cache: ${requestId}`);
    this.searchResults.delete(requestId);
  }

  /**
   * Clear all cached search results (for testing)
   */
  clearAllSearches(): void {
    logger.info('Clearing all cached search results');
    this.searchResults.clear();
  }
}

// Export singleton instance
export const singleSearchAPI = new SingleSearchAPIService();
